import {
  AnnualBilling,
  SubscriptionAccess,
  SubscriptionPlan,
} from '../types/Subscription';
import { FIVE_YEAR_BUY_INTENT } from '../services/subscriptions';

// Display prices for the paid Ora Pro tiers. These are display-only values and
// must be kept in sync with the Stripe Prices the backend actually charges
// (monthly V7 since ORA-381; annual and Student Life V6). Single source of
// truth — imported by the Pricing page and the PlanSelector.
export const ORA_PRO_MONTHLY_PRICE = 99;
export const ORA_PRO_ANNUAL_PRICE = 599;
export const ORA_PRO_5YEAR_PRICE = 1199;
export const ORA_PRO_5YEAR_YEARS = 5;
export const FREE_TRIAL_DAYS = 10;

export type PricingTierKey = 'monthly' | 'annual' | 'studentLife';

export interface PricingTier {
  key: PricingTierKey;
  title: string;
  basePrice: number;
  // Short label under the price, e.g. 'per month', 'per year'.
  unitLabel: string;
  // Compact unit shown in the "then $X{unit}" trial pill.
  trialUnit: string;
  product: SubscriptionAccess;
  plan?: SubscriptionPlan;
  annualBilling?: AnnualBilling;
  // Student Life is a one-time (5-year) purchase → FIVE_YEAR buy intent.
  isOneTime: boolean;
  // Whether legacy single-discount responses (no per-plan `plans` map) apply
  // to this tier. Per-plan payloads override this — School Launch (ORA-45)
  // discounts every tier, including the one-time Student Life purchase, each
  // with its own coupon.
  supportsDiscount: boolean;
  buyIntent: string;
}

/**
 * Builds the `buyIntent` string the backend persists and Onboarding decodes to
 * resume Stripe checkout. Mirrors the encoding the Auth page derives from the
 * `product`/`plan`/`annualBilling`/`oneTimeSku` URL params.
 */
export const buildBuyIntent = (tier: {
  product: SubscriptionAccess;
  plan?: SubscriptionPlan;
  annualBilling?: AnnualBilling;
  isOneTime: boolean;
}): string => {
  if (tier.isOneTime) return FIVE_YEAR_BUY_INTENT;
  let buyIntent = `${tier.product}_${tier.plan}`;
  if (tier.plan === SubscriptionPlan.ANNUAL && tier.annualBilling) {
    buyIntent += `_${tier.annualBilling}`;
  }
  return buyIntent;
};

const MONTHLY_TIER = {
  key: 'monthly' as const,
  title: 'Ora Pro Monthly',
  basePrice: ORA_PRO_MONTHLY_PRICE,
  unitLabel: 'per month',
  trialUnit: '/mo',
  product: SubscriptionAccess.STEP_SHELF,
  plan: SubscriptionPlan.MONTHLY,
  isOneTime: false,
  supportsDiscount: true,
};

const ANNUAL_TIER = {
  key: 'annual' as const,
  title: 'Ora Pro Annual',
  basePrice: ORA_PRO_ANNUAL_PRICE,
  unitLabel: 'per year',
  trialUnit: '/yr',
  product: SubscriptionAccess.STEP_SHELF,
  plan: SubscriptionPlan.ANNUAL,
  annualBilling: AnnualBilling.BILLED_YEARLY,
  isOneTime: false,
  supportsDiscount: true,
};

const STUDENT_LIFE_TIER = {
  key: 'studentLife' as const,
  title: 'Ora Pro Student Life',
  basePrice: ORA_PRO_5YEAR_PRICE,
  unitLabel: `once · ${ORA_PRO_5YEAR_YEARS} years of access`,
  trialUnit: ' once',
  product: SubscriptionAccess.STEP_SHELF,
  isOneTime: true,
  supportsDiscount: false,
};

export const ORA_PRO_TIERS: PricingTier[] = [
  MONTHLY_TIER,
  ANNUAL_TIER,
  STUDENT_LIFE_TIER,
].map((tier) => ({ ...tier, buyIntent: buildBuyIntent(tier) }));

// Single source of truth for the Free and Ora Pro feature lists. Both the
// Pricing page (Free card + Ora Pro card, mobile and desktop) and the /unlock
// PlanSelector render from these — update copy here only. Read the lists
// through `getFreeFeatures` / `getProFeatures` / `getAuthFlowFeatures`, which
// pick the NCLEX-RN (nursing) copy for an NCLEX primary exam and the medical
// copy for everyone else (ORA-952 D5).
export interface PricingFeature {
  label: string;
  // Optional nested detail lines rendered under the feature.
  subItems?: string[];
}

export const FREE_FEATURES: PricingFeature[] = [
  { label: 'Unlimited Flashcards' },
  {
    label: 'Limited Daily Content',
    subItems: ['1 Video', '5 QBank Questions'],
  },
  { label: '2,400+ Library Articles' },
  { label: 'Limited AI Study Recommendations' },
  { label: '3 Daily Lecture Uploads' },
];

export const ORA_PRO_FEATURES: PricingFeature[] = [
  {
    label: 'Unlimited Preclinical & Step 1 Content',
    subItems: ['6,000+ QBank Questions', '7,000+ Videos'],
  },
  {
    label: 'Unlimited Clinical Content',
    subItems: [
      '5,000+ Step 2 QBank Questions',
      '5,000+ Shelf Exam QBank Questions',
      '2,000+ Step 3 QBank Questions',
    ],
  },
  { label: '1,200+ COMLEX 1, 2, & 3 Questions' },
  { label: 'Timed QBank Mode' },
  { label: 'Full AI Study Recommendations' },
  { label: 'Full AI Chat' },
  { label: 'Unlimited Daily Lecture Uploads' },
  { label: 'Audio Summaries & Podcasts' },
  { label: 'Question Coach Walkthroughs' },
];

// Ora Pro for an NCLEX-RN primary exam. A nursing student never studies Step,
// Shelf or COMLEX, and NCLEX has no videos, games or Anki at launch, so none
// of the medical lines carry over. The AI companion and daily-plan lines here
// replace the medical "Full AI Chat" / "Full AI Study Recommendations" lines.
export const NCLEX_PRO_FEATURES: PricingFeature[] = [
  {
    label:
      'Full NCLEX-RN QBank with Next Generation item types (select all that apply, matrix, drop-down, dosage calculation)',
  },
  { label: '5,000+ cloze flashcards with spaced repetition' },
  {
    label:
      'Two full-length 85-question practice tests with performance bands by Client Needs',
  },
  { label: 'AI companion that knows nursing lab ranges' },
  { label: 'A daily study plan paced to your exam date' },
];

// The Free list for NCLEX-RN, derived from the medical one: no daily video
// (NCLEX has no videos), the nursing article count (439 at launch), and no
// lecture uploads (they map to the medical topic library today).
const NCLEX_FREE_FEATURES: PricingFeature[] = FREE_FEATURES.flatMap(
  (feature): PricingFeature[] => {
    if (feature.label === '3 Daily Lecture Uploads') return [];
    if (feature.label === '2,400+ Library Articles') {
      return [{ ...feature, label: '400+ Library Articles' }];
    }
    if (feature.subItems) {
      return [
        {
          ...feature,
          subItems: feature.subItems.filter((subItem) => subItem !== '1 Video'),
        },
      ];
    }
    return [feature];
  }
);

export const getFreeFeatures = (isNclex: boolean): PricingFeature[] =>
  isNclex ? NCLEX_FREE_FEATURES : FREE_FEATURES;

export const getProFeatures = (isNclex: boolean): PricingFeature[] =>
  isNclex ? NCLEX_PRO_FEATURES : ORA_PRO_FEATURES;

// Free features intentionally hidden in the auth-flow pricing display (they
// read as low-value / are superseded on a paid tier). Still shown on the main
// Pricing page's Free card.
const AUTH_FLOW_EXCLUDED_FREE_LABELS = new Set([
  'Limited Daily Content',
  '3 Daily Lecture Uploads',
  'Limited AI Study Recommendations',
]);

// The auth-flow PlanSelector lists the (trimmed) Free features followed by the
// Pro features on each paid tier (show the full value, not just "Everything in
// Free").
export const getAuthFlowFeatures = (isNclex: boolean): PricingFeature[] => [
  ...getProFeatures(isNclex),
  ...getFreeFeatures(isNclex).filter(
    (feature) => !AUTH_FLOW_EXCLUDED_FREE_LABELS.has(feature.label)
  ),
];
